import React, { useContext, useState } from 'react';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import { StaticImage } from 'gatsby-plugin-image';
import { EditorContext } from '@/features/editor/context/editor-context';
import { SmallButton } from '@/components/small-button';
import {
  ChangeTargetWrapper,
  PopoverWrapper,
} from '@/features/editor/widgets/changelog/shared/shared';
import { ElementTracking, Experience } from '@/webapi/use-experience-api';

export function ChangeVariant({ elemTrack }: { elemTrack?: ElementTracking }) {
  const {
    devicePreview: {
      editorState: { device },
    },
    experienceState: { currentExperience },
  } = useContext(EditorContext);

  const [isOpen, setIsOpen] = useState(false);

  const onClick = (ev) => {
    ev.stopPropagation();
    ev.preventDefault();
    setIsOpen(true);
  };

  const onSelected = () => {
    setIsOpen(false);
  };

  return (
    <ChangeTargetWrapper device={device} allowEdit>
      <span>for</span>
      <Popover
        onClickOutside={() => setIsOpen(!isOpen)}
        positions={[`bottom`, `left`, `top`, `right`]}
        isOpen={isOpen}
        containerStyle={{ zIndex: `10000` }}
        content={({ position, childRect, popoverRect }) => (
          <ArrowContainer
            position={position}
            childRect={childRect}
            popoverRect={popoverRect}
            arrowColor="white"
            arrowClassName="change-target-arrow"
            arrowSize={10}
          >
            <ChangeVariantPopOver
              onSelection={onSelected}
              elemTrack={elemTrack}
            />
          </ArrowContainer>
        )}
      >
        <SmallButton
          size="medium"
          background="rgba(234, 237, 239, 0.8)"
          noShadow
          fontFamily="Inter"
          color="#000000"
          onClick={onClick}
        >
          {isForASingleVariant(elemTrack)
            ? getSelectedVariantName(currentExperience, elemTrack)
            : `All Variants`}
          <StaticImage
            imgStyle={{ display: `block` }}
            src="../../../../../assets/down_arrow.svg"
            alt="down arrow"
            placeholder="none"
            loading="eager"
            height={5}
          />
        </SmallButton>
      </Popover>
    </ChangeTargetWrapper>
  );
}

const findVariantId = (currentExperience: Experience, selection) =>
  currentExperience?.variants.find((v) => v.name === selection)?.id;

function ChangeVariantPopOver({
  onSelection,
  elemTrack,
}: {
  onSelection: (placement: any) => void;
  elemTrack?: ElementTracking;
}) {
  const {
    experienceState: { currentExperience, setElementTracking },
  } = useContext(EditorContext);

  const variantNames = currentExperience?.variants.map((v) => v.name);

  const selectedVariant = elementTrackingSelection(
    currentExperience,
    elemTrack,
  );

  const onClick = (ev, selection) => {
    ev.stopPropagation();
    ev.preventDefault();
    setElementTracking({
      ...elemTrack,
      variant:
        selection === `Control`
          ? `Control`
          : findVariantId(currentExperience, selection),
    } as any);
    onSelection(selection);
  };

  return (
    <PopoverWrapper>
      {[`All Variants`, `Control`, ...variantNames].map((selection) => (
        <div
          key={selection}
          onClick={(ev) => {
            onClick(ev, selection);
          }}
          className={
            findVariantId(currentExperience, selection) === selectedVariant ||
            selection === selectedVariant
              ? `vsly-selected`
              : ``
          }
        >
          <StaticImage
            placeholder="none"
            height={15}
            src="../../../../../assets/blue_v.svg"
            alt="blue checkmark"
          />
          {selection}
        </div>
      ))}
    </PopoverWrapper>
  );
}

const getSelectedVariantName = (
  currentExperience: Experience,
  elemTrack: ElementTracking,
) => {
  const id = elemTrack?.variant;
  const name = currentExperience?.variants?.find((v) => v.id === id)?.name;
  return name || id;
};

const isForASingleVariant = (elemTrack: ElementTracking) =>
  elemTrack?.variant?.length > 0;

const elementTrackingSelection = (
  currentExperience: Experience,
  elemTrack: ElementTracking,
) => {
  let selectedVariant = currentExperience?.variants.find(
    (v) => v.id === elemTrack?.variant,
  )?.name;
  if (elemTrack?.variant === `Control`) {
    selectedVariant = `Control`;
  }
  if (!elemTrack?.variant) {
    selectedVariant = `All Variants`;
  }
  return selectedVariant;
};
